import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [tries, setTries] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let active = true;
    const poll = async (n) => {
      if (!active) return;
      if (n > 8) { setStatus("failed"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      setTries(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { active = false; };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      {status === "checking" && (
        <>
          <Loader2 size={56} className="mx-auto text-amber-500 animate-spin" />
          <h1 className="font-head font-black text-3xl mt-5 text-slate-50">Confirming payment...</h1>
          <p className="text-slate-400 mt-2">Please wait a moment{".".repeat((tries % 3) + 1)}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={64} className="mx-auto text-emerald-400" />
          <h1 className="font-head font-black text-3xl mt-5 text-slate-50">Subscription active!</h1>
          <p className="text-slate-400 mt-2">Your plan is now live and your listing visibility has been boosted. 🚀</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-11 px-8">
            Go to Dashboard
          </Button>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle size={64} className="mx-auto text-red-400" />
          <h1 className="font-head font-black text-3xl mt-5 text-slate-50">Payment not confirmed</h1>
          <p className="text-slate-400 mt-2">We couldn't confirm your payment. If you were charged it will still activate shortly.</p>
          <Button onClick={() => navigate("/pricing")} className="mt-6 bg-slate-800 hover:bg-slate-700 rounded-xl h-11 px-8">
            Back to Pricing
          </Button>
        </>
      )}
    </div>
  );
}
